import React, { useMemo, useState } from "react";
import { TextInput } from "@SeemplicityDev/shared";
import { getListOptions } from "../../../Findings/FindingsFilters/FilterRow/FilterRowUtils";
import { useFilterBuilderContext } from "./FilterBuilderContext";
import { filterTypes } from "../../../Findings/FindingsUtils";
import FilterFieldsSearch, {
  type FilterFieldsSearchValue,
} from "./FilterFieldsSearch/FilterFieldsSearch";
import useRecentlyUsedFilters from "../useRecentlyUsedFilters";
import FilterChipButton from "../FilterChip/FilterChipButton";
import type {
  FieldData,
  FilterItem,
} from "../../FilterManagement/CommonFilterTypes";
import {
  generalCategoryField,
  groupFieldsByCategory,
  type FilterFieldCategory,
  type FilterFieldOption,
} from "./groupFieldsByCategory";
import FilterFieldCategoryList from "./FilterFieldCategoryList";
import FilterEntitySection from "./FilterEntitySection";
import {
  filterKeyEntityEnum,
  isBareFilterKey,
  isSameRemediationItemFilterKey,
  splitFilterKey,
  type FilterKeyEntity,
} from "../../FilterManagement/filterKey";
import { filterEntityEnum } from "../../FilterManagement/filterEntity";

const minSearchLengthScope = 3;
const minSearchLengthFilter = 1;

const filterEntitySectionOrder: readonly FilterKeyEntity[] = [
  filterKeyEntityEnum.REMEDIATION_ITEM,
  filterKeyEntityEnum.FINDING,
];

const getEntitySectionRank = (field: FieldData): number => {
  const { entity } = splitFilterKey(field.field);

  return entity
    ? filterEntitySectionOrder.indexOf(entity)
    : filterEntitySectionOrder.length;
};

const filterEntityGeneralCategoryLabels: Partial<
  Record<FilterKeyEntity, string>
> = {
  [filterKeyEntityEnum.REMEDIATION_ITEM]: "Attributes",
};

const relabelEntityCategories = (
  entity: FilterKeyEntity,
  categories: FilterFieldCategory[]
): FilterFieldCategory[] => {
  const generalCategoryLabel = filterEntityGeneralCategoryLabels[entity];

  if (!generalCategoryLabel) {
    return categories;
  }

  return categories.map((category) =>
    category.field === generalCategoryField
      ? { ...category, label: generalCategoryLabel }
      : category
  );
};

interface FilterFieldEntityGroup {
  entity: FilterKeyEntity;
  categories: FilterFieldCategory[];
}

const FilterFieldSelection: React.FC = () => {
  const [searchValue, setSearchValue] = useState("");

  const {
    filterBuilderState: {
      fieldsData,
      context,
      entity,
      onFilterSelect,
      statusesData,
      filtersToPin,
    },
  } = useFilterBuilderContext();

  const { getRecentlyUsedFilters } = useRecentlyUsedFilters({
    filterEntity: entity ?? undefined,
    filtersToPin,
  });

  const recentlyUsedFilters = useMemo<FilterItem[]>(() => {
    return getRecentlyUsedFilters();
  }, [getRecentlyUsedFilters]);

  const isScope = useMemo(() => context === filterTypes.SCOPE, [context]);

  const hasFieldSearch = isScope || entity !== filterEntityEnum.RESOURCE;

  const minSearchLength = isScope
    ? minSearchLengthScope
    : minSearchLengthFilter;

  const isSearching = searchValue.length >= minSearchLength;

  const selectableFieldsData = useMemo(() => {
    const pinnedFields = filtersToPin?.map((f) => f.filterField) ?? [];
    return fieldsData?.filter(
      (field) =>
        !pinnedFields.some((pinnedField) =>
          isSameRemediationItemFilterKey(pinnedField, field.field)
        )
    );
  }, [fieldsData, filtersToPin]);

  const fieldsByCategory = useMemo<FilterFieldCategory[]>(() => {
    if (isScope) {
      return getListOptions(
        selectableFieldsData,
        false
      ) as FilterFieldCategory[];
    }

    return groupFieldsByCategory(
      [...(selectableFieldsData ?? [])].sort(
        (a, b) => getEntitySectionRank(a) - getEntitySectionRank(b)
      )
    );
  }, [selectableFieldsData, isScope]);

  const entitySections = useMemo<FilterFieldEntityGroup[]>(() => {
    if (isScope) {
      return [];
    }

    return filterEntitySectionOrder
      .map((sectionEntity) => ({
        entity: sectionEntity,
        categories: relabelEntityCategories(
          sectionEntity,
          groupFieldsByCategory(
            selectableFieldsData?.filter(
              (field) => splitFilterKey(field.field).entity === sectionEntity
            )
          )
        ),
      }))
      .filter(({ categories }) => !!categories.length);
  }, [selectableFieldsData, isScope]);

  const unprefixedCategories = useMemo<FilterFieldCategory[]>(() => {
    if (!entitySections.length) {
      return [];
    }

    return groupFieldsByCategory(
      selectableFieldsData?.filter((field) => isBareFilterKey(field.field))
    );
  }, [selectableFieldsData, entitySections]);

  const matchingFieldsByCategory = useMemo<FilterFieldCategory[]>(() => {
    if (isScope || !isSearching) {
      return [];
    }

    return (
      fieldsByCategory?.reduce<FilterFieldCategory[]>((acc, category) => {
        const matchingSubItems =
          category.subItems?.filter((subItem) => {
            return subItem.label
              ?.toLowerCase()
              ?.includes(searchValue?.toLowerCase());
          }) ?? [];

        if (!matchingSubItems.length) {
          return acc;
        }

        return [...acc, { ...category, subItems: matchingSubItems }];
      }, []) ?? []
    );
  }, [fieldsByCategory, searchValue, isScope, isSearching]);

  const flattenedFields = useMemo<FilterFieldOption[]>(() => {
    return fieldsByCategory?.reduce<FilterFieldOption[]>((acc, category) => {
      return [...acc, ...category.subItems];
    }, []);
  }, [fieldsByCategory]);

  const handleFilterFieldValueSelect = (
    filtersArray: FilterFieldsSearchValue[]
  ) => {
    // The scope search emits `{ field, values }` items; `onFilterSelect` is
    // typed for `FilterItem` but accepts this shape at runtime (FilterRowUtils
    // builder normalizes it downstream). Cast bridges the two value shapes.
    onFilterSelect(filtersArray as unknown as FilterItem[]);
  };

  const visibleCategories = isSearching
    ? matchingFieldsByCategory
    : fieldsByCategory;

  const isEntityBrowse = !isScope && !isSearching && !!entitySections.length;

  return (
    <div className={"filter-field-selection"} data-testid={"field-selection"}>
      {!isScope && !!recentlyUsedFilters?.length && (
        <div className={"recently-used-filters-wrapper"}>
          <span className={"recently-used-filters-title"}>Last used</span>
          <div className={"recently-used-filters"}>
            {recentlyUsedFilters?.map((filter) => (
              <React.Fragment key={filter.field}>
                <FilterChipButton
                  filterItem={filter}
                  isActive={false}
                  isBlink={false}
                  onClick={() => onFilterSelect(filter)}
                  displayFullValue={false}
                  allowRemove={false}
                  handleRemoveFilter={() => {}}
                  isEditable={true}
                  fieldsData={fieldsData}
                  statusesData={statusesData}
                  filtersToPin={[]}
                />
              </React.Fragment>
            ))}
          </div>
        </div>
      )}
      {hasFieldSearch && (
        <TextInput
          data-testid={"filter-field-search"}
          value={searchValue}
          wrapperClassName={"filter-field-search-input no-padding-bottom"}
          placeholder={
            isScope ? "e.g. account name, project name" : "Type to search..."
          }
          // Runtime preserved: passing `null` (not undefined) drives TextInput's
          // `has-label` class toggle (`label !== null`), so keep the literal null
          // and only widen the type to the prop's `string | undefined`.
          label={(isScope ? "Search for value" : null) as string | undefined}
          isClearable
          leftIcon={"seem-icon-search"}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setSearchValue(e?.target?.value || "")
          }
          onClear={(e: React.SyntheticEvent) => e.stopPropagation()}
          hideLabelHelper
          labelTop
          applyAutoFocus
        />
      )}
      <div
        className={`filter-categories-wrapper ${isSearching ? "is-searching" : ""}`}
      >
        {isScope && isSearching ? (
          <FilterFieldsSearch
            resourceName={searchValue}
            onChange={handleFilterFieldValueSelect}
            clearSearchValue={() => setSearchValue("")}
            fieldOptions={flattenedFields}
          />
        ) : (
          <>
            {isEntityBrowse && (
              <>
                {entitySections.map(
                  ({ entity: sectionEntity, categories }, sectionIndex) => (
                    <FilterEntitySection
                      key={sectionEntity}
                      entity={sectionEntity}
                    >
                      <FilterFieldCategoryList
                        categories={categories}
                        isScope={false}
                        isSearching={false}
                        searchValue={searchValue}
                        isInitiallyCollapsed={sectionIndex > 0}
                      />
                    </FilterEntitySection>
                  )
                )}
                <FilterFieldCategoryList
                  categories={unprefixedCategories}
                  isScope={false}
                  isSearching={false}
                  searchValue={searchValue}
                  isInitiallyCollapsed
                />
              </>
            )}
            {!isEntityBrowse && (
              <FilterFieldCategoryList
                categories={visibleCategories ?? []}
                isScope={isScope}
                isSearching={isSearching}
                searchValue={searchValue}
              />
            )}
            {isSearching && !visibleCategories?.length && (
              <span
                className={"no-match-found"}
                data-testid={"filter-search-no-result"}
              >
                No results
              </span>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default FilterFieldSelection;
